import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Mail, Lock, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import GoogleSignInButton from "@/components/shared/GoogleSignInButton";
import { toast } from "sonner";

export const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const validateEmail = (v) => EMAIL_RX.test(v.trim());
export const validatePassword = (v) => v.length >= 8;

export default function LoginPage() {
    const { login, loginWithGoogle, isAuthenticated, isLoading, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    if (isLoading) return null;
    if (isAuthenticated) {
        const target =
            user?.role === "admin"
                ? "/admin"
                : user?.role === "merchant"
                ? "/dashboard"
                : location.state?.from || "/";
        return <Navigate to={target} replace />;
    }

    const validate = () => {
        const e = {};
        if (!email.trim()) e.email = "Ingresa tu correo.";
        else if (!validateEmail(email)) e.email = "Correo inválido.";
        if (!password) e.password = "Ingresa tu contraseña.";
        return e;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setSubmitting(true);
        setServerError(null);
        try {
            const user = await login({ email: email.trim(), password });
            redirectAfter(user);
        } catch (err) {
            setServerError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const redirectAfter = (u) => {
        if (u.role === "admin") navigate("/admin", { replace: true });
        else if (u.role === "merchant") navigate("/dashboard", { replace: true });
        else navigate(location.state?.from || "/", { replace: true });
    };

    const handleGoogle = async (credential) => {
        setServerError(null);
        try {
            const u = await loginWithGoogle(credential);
            redirectAfter(u);
        } catch (err) {
            setServerError(err.message);
        }
    };

    return (
        <div
            data-testid="login-page"
            className="min-h-[calc(100vh-72px)] grid place-items-center px-4 py-10 sm:py-16 overflow-x-hidden"
        >
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-md min-w-0"
            >
                <div className="text-center mb-8">
                    <Link
                        to="/"
                        aria-label="Tiendify — Inicio"
                        className="inline-flex items-center justify-center mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 rounded-md"
                    >
                        <img
                            src="/assets/images/t-mark.png"
                            alt="Tiendify"
                            width="48"
                            height="48"
                            className="w-12 h-12 object-contain"
                        />
                    </Link>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
                        Bienvenido de vuelta
                    </h1>
                    <p className="mt-2 text-sm text-zinc-600">
                        Inicia sesión para continuar en Tiendify.
                    </p>
                </div>

                <form
                    onSubmit={onSubmit}
                    noValidate
                    className="bg-white border border-zinc-200 rounded-2xl p-5 sm:p-8 shadow-sm space-y-5 min-w-0"
                >
                    <GoogleSignInButton
                        onCredential={handleGoogle}
                        onError={(e) => {
                            console.error(e);
                            toast.error("No se pudo iniciar sesión con Google");
                        }}
                    />

                    <div className="flex items-center gap-3 text-xs text-zinc-600">
                        <div className="flex-1 h-px bg-zinc-200" />
                        <span>o con tu correo</span>
                        <div className="flex-1 h-px bg-zinc-200" />
                    </div>

                    <Field
                        id="email"
                        label="Correo electrónico"
                        icon={Mail}
                        error={errors.email}
                        testid="login-email"
                    >
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={() =>
                                setErrors((p) => ({ ...p, email: validate().email }))
                            }
                            placeholder="tu@correo.com"
                            autoComplete="email"
                            data-testid="login-email-input"
                            className="pl-10 h-11"
                        />
                    </Field>

                    <Field
                        id="password"
                        label="Contraseña"
                        icon={Lock}
                        error={errors.password}
                        testid="login-password"
                        rightAction={
                            <button
                                type="button"
                                onClick={() => setShowPwd((s) => !s)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                                aria-label={showPwd ? "Ocultar" : "Mostrar"}
                            >
                                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        }
                    >
                        <Input
                            id="password"
                            type={showPwd ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            data-testid="login-password-input"
                            className="pl-10 pr-10 h-11"
                        />
                    </Field>

                    {serverError && (
                        <p
                            data-testid="login-error"
                            className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
                        >
                            {serverError}
                        </p>
                    )}

                    <Button
                        type="submit"
                        data-testid="login-submit"
                        disabled={submitting}
                        className="w-full h-11 bg-primary hover:bg-primary-hover text-primary-foreground font-medium"
                    >
                        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Iniciar sesión
                    </Button>
                </form>

                <p className="mt-6 text-center text-sm text-zinc-600">
                    ¿No tienes cuenta?{" "}
                    <Link
                        to="/registro"
                        data-testid="login-go-register"
                        className="font-medium text-primary hover:text-primary-hover"
                    >
                        Regístrate gratis
                    </Link>
                </p>
            </motion.div>
        </div>
    );
}

function Field({ id, label, icon: Icon, error, testid, rightAction, children }) {
    return (
        <div data-testid={testid}>
            <Label htmlFor={id} className="text-xs font-medium text-zinc-700">
                {label}
            </Label>
            <div className="relative mt-1.5">
                {Icon && (
                    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                )}
                {children}
                {rightAction}
            </div>
            {error && (
                <p className="mt-1.5 text-xs text-red-600" data-testid={`${testid}-error`}>
                    {error}
                </p>
            )}
        </div>
    );
}

